import React, { useState, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon, BotIcon, UserIcon, MapPinIcon, WalletIcon, ClockIcon, CarIcon, CheckCircle2Icon, TrendingUpIcon, LightbulbIcon, ArrowDownIcon, TargetIcon, PhoneIcon, MailIcon, MessageCircleIcon, FileTextIcon, ListIcon, StickyNoteIcon, ShieldCheckIcon, ShieldAlertIcon, PlusIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon } from 'lucide-react';
export const PIPELINE_STAGES = ['Discovered', 'Qualified', 'Interested', 'Negotiating', 'Ready To Buy', 'Won', 'Lost'] as const;
export type LeadStatus = (typeof PIPELINE_STAGES)[number];
export const OWNERS = ['Unassigned', 'Fadi', 'Sara', 'Sales Team'] as const;
export interface Lead {
  name: string;
  intentScore: number;
  lookingFor: string;
  budget: string;
  timeline: string;
  location: string;
  matchedItem: string;
  status: LeadStatus;
  summary: string;
  phone?: string;
  email?: string;
  preferredContact?: string;
  preferredLanguage?: string;
  source?: string;
  lastActive?: string;
  receivedDate?: string;
  owner?: string;
  nextAction?: string;
  nextActionDue?: string;
  consent?: 'shared' | 'requested' | 'not_shared';
}
interface LeadDetailModalProps {
  lead: Lead;
  onClose: () => void;
  onStatusChange?: (status: LeadStatus) => void;
  onOwnerChange?: (owner: string) => void;
}
type DetailTab = 'Overview' | 'Timeline' | 'Details' | 'Matches' | 'AI' | 'Activity' | 'Notes';
function intentBucket(score: number) {
  if (score >= 90) return {
    label: 'Ready To Buy',
    color: 'text-green-700',
    bg: 'bg-green-100',
    bar: 'bg-green-500'
  };
  if (score >= 75) return {
    label: 'High Intent',
    color: 'text-primary-indigo',
    bg: 'bg-indigo-100',
    bar: 'bg-primary-indigo'
  };
  if (score >= 50) return {
    label: 'Interested',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    bar: 'bg-amber-500'
  };
  return {
    label: 'Early Stage',
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    bar: 'bg-slate-400'
  };
}
export function LeadDetailModal({
  lead,
  onClose,
  onStatusChange,
  onOwnerChange
}: LeadDetailModalProps) {
  const bucket = intentBucket(lead.intentScore);
  const [tab, setTab] = useState<DetailTab>('Overview');
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [owner, setOwner] = useState<string>(lead.owner ?? 'Unassigned');
  const [notes, setNotes] = useState('Customer prefers white GCC vehicles only. Do not suggest imported vehicles.');
  const [activity, setActivity] = useState([{
    text: 'Lead created from Talab AI conversation',
    time: '2 days ago'
  }, {
    text: 'Customer asked about delivery options',
    time: '2 days ago'
  }, {
    text: 'Customer requested seller contact',
    time: '1 day ago'
  }, {
    text: `Owner set to ${lead.owner ?? 'Sara'}`,
    time: '1 day ago'
  }]);
  const hasContact = lead.consent === 'shared';
  const changeStatus = (next: LeadStatus) => {
    if (next === status) return;
    setActivity(a => [{
      text: `Status changed from ${status} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setStatus(next);
    onStatusChange?.(next);
  };
  const changeOwner = (next: string) => {
    if (next === owner) return;
    setActivity(a => [{
      text: `Owner changed from ${owner} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setOwner(next);
    onOwnerChange?.(next);
  };
  const tabs: {
    id: DetailTab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1568" />
  }, {
    id: 'Timeline',
    icon: <MessageCircleIcon className="w-4 h-4" data-id="element-1569" />
  }, {
    id: 'Details',
    icon: <UserIcon className="w-4 h-4" data-id="element-1570" />
  }, {
    id: 'Matches',
    icon: <CarIcon className="w-4 h-4" data-id="element-1571" />
  }, {
    id: 'AI',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1572" />
  }, {
    id: 'Activity',
    icon: <ListIcon className="w-4 h-4" data-id="element-1573" />
  }, {
    id: 'Notes',
    icon: <StickyNoteIcon className="w-4 h-4" data-id="element-1574" />
  }];
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-1575">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      stiffness: 340,
      damping: 34
    }} className="absolute inset-0 z-[100] bg-slate-50 flex flex-col" data-id="element-1576">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100" data-id="element-1577">
          <div className="flex items-center gap-3 px-2 py-3" data-id="element-1578">
            <button onClick={onClose} className="p-2 text-slate-400 active:bg-slate-50 rounded-full transition-colors" data-id="element-1579">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-1580" />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-1581">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white font-bold flex-shrink-0" data-id="element-1582">
                {lead.name.charAt(0)}
              </div>
              <div className="min-w-0" data-id="element-1583">
                <h2 className="text-base font-display font-bold text-dark-navy truncate" data-id="element-1584">
                  {lead.name}
                </h2>
                <div className="flex items-center gap-1.5 mt-0.5" data-id="element-1585">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${bucket.bg} ${bucket.color}`} data-id="element-1586">
                    {bucket.label} · {lead.intentScore}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex gap-2 px-4 pb-3" data-id="element-1587">
            <div className="flex-1" data-id="element-1588">
              <Dropdown label="Status" value={status} options={PIPELINE_STAGES as unknown as string[]} onChange={v => changeStatus(v as LeadStatus)} data-id="element-1589" />
            </div>
            <div className="flex-1" data-id="element-1590">
              <Dropdown label="Owner" value={owner} options={OWNERS as unknown as string[]} onChange={changeOwner} data-id="element-1591" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto gap-2 px-4 pb-3 hide-scrollbar" data-id="element-1592">
            {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${tab === t.id ? 'bg-indigo-50 text-primary-indigo' : 'bg-slate-50 text-slate-600'}`} data-id="element-1593">
                <span className={tab === t.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-1594">
                  {t.icon}
                </span>
                {t.id}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-5" data-id="element-1595">
          <AnimatePresence mode="wait" data-id="element-1596">
            <motion.div key={tab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-1597">
              {tab === 'Overview' && <div className="space-y-4" data-id="element-1598">
                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1599">
                    <h3 className="text-sm font-bold text-dark-navy mb-3 flex items-center gap-2" data-id="element-1600">
                      <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1601" /> AI
                      Summary
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-1602">
                      {lead.summary}
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1603">
                    <h3 className="text-sm font-bold text-dark-navy mb-3" data-id="element-1604">
                      Key Requirements
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1605">
                      <Fact icon={<CarIcon data-id="element-1607" />} label="Looking for" value={lead.lookingFor} data-id="element-1606" />
                      <Fact icon={<WalletIcon data-id="element-1609" />} label="Budget" value={lead.budget} data-id="element-1608" />
                      <Fact icon={<ClockIcon data-id="element-1611" />} label="Timeline" value={lead.timeline} data-id="element-1610" />
                      <Fact icon={<MapPinIcon data-id="element-1613" />} label="Location" value={lead.location} data-id="element-1612" />
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1614">
                    <h3 className="text-sm font-bold text-dark-navy mb-3" data-id="element-1615">
                      Next Action
                    </h3>
                    <div className="flex items-center justify-between p-3 bg-indigo-50/50 rounded-xl border border-indigo-100" data-id="element-1616">
                      <div data-id="element-1617">
                        <p className="text-sm font-semibold text-primary-indigo" data-id="element-1618">
                          {lead.nextAction}
                        </p>
                        <p className="text-xs text-indigo-600/70 mt-0.5" data-id="element-1619">
                          Due: {lead.nextActionDue}
                        </p>
                      </div>
                      <button className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm text-primary-indigo" data-id="element-1620">
                        <CheckIcon className="w-4 h-4" data-id="element-1621" />
                      </button>
                    </div>
                  </div>
                </div>}

              {tab === 'Timeline' && <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1622">
                  <div className="space-y-6" data-id="element-1623">
                    <TimelineItem role="user" text="I'm looking for a family SUV, preferably GCC specs." time="2 days ago" data-id="element-1624" />
                    <TimelineItem role="ai" text="I found 14 SUVs matching your criteria. The Toyota Prado GXR 2018 is a strong match. Would you like to see the details?" time="2 days ago" data-id="element-1625" />
                    <TimelineItem role="user" text="Yes, what's the mileage and price?" time="1 day ago" data-id="element-1626" />
                    <TimelineItem role="ai" text="It has 112,000 km and is priced at AED 82,000. It's sold by a Verified Business." time="1 day ago" data-id="element-1627" />
                    <TimelineItem role="user" text="Can I speak to the seller?" time="1 day ago" data-id="element-1628" />
                    <TimelineItem role="system" text="Customer requested seller contact. Lead qualified and routed to Sales Team." time="1 day ago" data-id="element-1629" />
                  </div>
                </div>}

              {tab === 'Details' && <div className="space-y-4" data-id="element-1630">
                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1631">
                    <div className="flex items-center justify-between mb-4" data-id="element-1632">
                      <h3 className="text-sm font-bold text-dark-navy" data-id="element-1633">
                        Contact Info
                      </h3>
                      {hasContact ? <span className="px-2 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded-md flex items-center gap-1" data-id="element-1634">
                          <ShieldCheckIcon className="w-3 h-3" data-id="element-1635" /> Shared
                        </span> : <span className="px-2 py-1 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-md flex items-center gap-1" data-id="element-1636">
                          <ShieldAlertIcon className="w-3 h-3" data-id="element-1637" /> Not shared
                        </span>}
                    </div>

                    {hasContact ? <div className="space-y-3" data-id="element-1638">
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-1639">
                          <PhoneIcon className="w-4 h-4 text-slate-400" data-id="element-1640" />
                          <div className="flex-1" data-id="element-1641">
                            <p className="text-[10px] text-slate-400 font-medium" data-id="element-1642">
                              Phone
                            </p>
                            <p className="text-sm font-semibold text-dark-navy" data-id="element-1643">
                              {lead.phone}
                            </p>
                          </div>
                          <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 shadow-sm" data-id="element-1644">
                            Call
                          </button>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-1645">
                          <MailIcon className="w-4 h-4 text-slate-400" data-id="element-1646" />
                          <div className="flex-1" data-id="element-1647">
                            <p className="text-[10px] text-slate-400 font-medium" data-id="element-1648">
                              Email
                            </p>
                            <p className="text-sm font-semibold text-dark-navy" data-id="element-1649">
                              {lead.email}
                            </p>
                          </div>
                          <button className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 shadow-sm" data-id="element-1650">
                            Email
                          </button>
                        </div>
                      </div> : <div className="text-center py-6 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1651">
                        <ShieldAlertIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-1652" />
                        <p className="text-sm text-slate-600 mb-3 px-4" data-id="element-1653">
                          Customer has not requested seller contact.
                        </p>
                        <button className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl shadow-sm" data-id="element-1654">
                          Request Contact Details
                        </button>
                      </div>}
                  </div>

                  <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1655">
                    <h3 className="text-sm font-bold text-dark-navy mb-3" data-id="element-1656">
                      Preferences
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1657">
                      <Fact icon={<MessageCircleIcon data-id="element-1659" />} label="Preferred Contact" value={lead.preferredContact ?? 'Unknown'} data-id="element-1658" />
                      <Fact icon={<GlobeIcon data-id="element-1661" />} label="Language" value={lead.preferredLanguage ?? 'Unknown'} data-id="element-1660" />
                      <Fact icon={<TargetIcon data-id="element-1663" />} label="Source" value={lead.source ?? 'Unknown'} data-id="element-1662" />
                      <Fact icon={<ClockIcon data-id="element-1665" />} label="Last Active" value={lead.lastActive ?? 'Unknown'} data-id="element-1664" />
                    </div>
                  </div>
                </div>}

              {tab === 'Matches' && <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1666">
                  <h3 className="text-sm font-bold text-dark-navy mb-3" data-id="element-1667">
                    Primary Match
                  </h3>
                  <div className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-1668">
                    <div className="w-16 h-16 rounded-lg bg-slate-200 flex-shrink-0 overflow-hidden" data-id="element-1669">
                      <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=200" alt="" className="w-full h-full object-cover" data-id="element-1670" />
                    </div>
                    <div data-id="element-1671">
                      <p className="text-xs font-bold text-primary-indigo mb-0.5" data-id="element-1672">
                        98% Match
                      </p>
                      <p className="text-sm font-bold text-dark-navy leading-tight mb-1" data-id="element-1673">
                        {lead.matchedItem}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1674">AED 82,000</p>
                    </div>
                  </div>
                </div>}

              {tab === 'AI' && <div className="space-y-4" data-id="element-1675">
                  <div className="bg-ai-purple/5 border border-ai-purple/10 rounded-2xl p-4" data-id="element-1676">
                    <h3 className="text-sm font-bold text-dark-navy mb-3 flex items-center gap-2" data-id="element-1677">
                      <LightbulbIcon className="w-4 h-4 text-ai-purple" data-id="element-1678" />{' '}
                      Suggested Actions
                    </h3>
                    <ul className="space-y-2" data-id="element-1679">
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-1680">
                        <span className="w-1.5 h-1.5 rounded-full bg-ai-purple mt-1.5 flex-shrink-0" data-id="element-1681" />
                        Send a walkaround video of the Prado to build trust.
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-1682">
                        <span className="w-1.5 h-1.5 rounded-full bg-ai-purple mt-1.5 flex-shrink-0" data-id="element-1683" />
                        Highlight the GCC specs, as this is a strong preference.
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-1684">
                        <span className="w-1.5 h-1.5 rounded-full bg-ai-purple mt-1.5 flex-shrink-0" data-id="element-1685" />
                        Offer a test drive this weekend.
                      </li>
                    </ul>
                  </div>
                </div>}

              {tab === 'Activity' && <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm" data-id="element-1686">
                  <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-[11px] before:w-px before:bg-slate-100" data-id="element-1687">
                    {activity.map((a, i) => <div key={i} className="relative flex gap-3" data-id="element-1688">
                        <div className="w-6 h-6 rounded-full bg-slate-50 border-2 border-white flex items-center justify-center flex-shrink-0 z-10" data-id="element-1689">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-1690" />
                        </div>
                        <div className="pt-0.5" data-id="element-1691">
                          <p className="text-sm text-slate-700" data-id="element-1692">{a.text}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-1693">
                            {a.time}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}

              {tab === 'Notes' && <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex flex-col h-64" data-id="element-1694">
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes about this lead..." className="flex-1 w-full resize-none outline-none text-sm text-slate-700 placeholder:text-slate-400" data-id="element-1695" />
                  <div className="flex justify-end pt-3 border-t border-slate-100" data-id="element-1696">
                    <button className="px-4 py-2 bg-primary-indigo text-white text-xs font-bold rounded-xl shadow-sm" data-id="element-1697">
                      Save Note
                    </button>
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>, mountNode);
}
function Dropdown({
  label,
  value,
  options,
  onChange
}: any) {
  return <div className="relative" data-id="element-1698">
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full appearance-none pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1699">
        {options.map((o: string) => <option key={o} value={o} data-id="element-1700">
            {o}
          </option>)}
      </select>
      <span className="absolute left-2 -top-2 px-1 bg-white text-[9px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-1701">
        {label}
      </span>
      <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1702" />
    </div>;
}
function Fact({
  icon,
  label,
  value
}: any) {
  return <div className="flex items-start gap-2" data-id="element-1703">
      <div className="text-slate-400 mt-0.5" data-id="element-1704">
        {icon && cloneElement(icon, {
        className: 'w-3.5 h-3.5'
      })}
      </div>
      <div data-id="element-1705">
        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5" data-id="element-1706">
          {label}
        </p>
        <p className="text-sm font-semibold text-dark-navy" data-id="element-1707">{value}</p>
      </div>
    </div>;
}
function TimelineItem({
  role,
  text,
  time
}: any) {
  const isAi = role === 'ai';
  const isSystem = role === 'system';
  return <div className={`flex gap-3 ${isSystem ? 'justify-center' : ''}`} data-id="element-1708">
      {!isSystem && !isAi && <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1709">
          <UserIcon className="w-4 h-4 text-slate-500" data-id="element-1710" />
        </div>}
      {!isSystem && isAi && <div className="w-8 h-8 rounded-full bg-ai-purple/10 flex items-center justify-center flex-shrink-0" data-id="element-1711">
          <BotIcon className="w-4 h-4 text-ai-purple" data-id="element-1712" />
        </div>}
      <div className={isSystem ? 'text-center' : 'flex-1'} data-id="element-1713">
        <div className={`p-3 rounded-2xl text-sm ${isSystem ? 'bg-slate-50 text-slate-500 text-xs inline-block' : isAi ? 'bg-ai-purple/5 text-slate-700 rounded-tl-sm' : 'bg-slate-100 text-slate-700 rounded-tr-sm'}`} data-id="element-1714">
          {text}
        </div>
        {!isSystem && <p className="text-[10px] text-slate-400 mt-1 ml-1" data-id="element-1715">{time}</p>}
      </div>
    </div>;
}
function GlobeIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1716">
      <circle cx="12" cy="12" r="10" data-id="element-1717" />
      <line x1="2" y1="12" x2="22" y2="12" data-id="element-1718" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" data-id="element-1719" />
    </svg>;
}